import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { usePWA } from '../context/PWAContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { UpgradeBanner } from '../components/UpgradeModal';
import QuotaExhaustedPopup from '../components/QuotaExhaustedPopup';
import { ShareButton } from '../components/ShareCard';
import axios from 'axios';
import { 
  Camera, ArrowLeft, Loader2, Sparkles, Hand, 
  Heart, Briefcase, Baby, Activity, Coins, 
  RefreshCw, Sun, ChevronDown, ChevronUp,
  Upload, Scan, Star, Crown, Brain, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Palm reading result sections with icons
const PALM_SECTIONS = [
  { key: 'life', label: 'Life Path', icon: Sun, color: 'from-amber-500 to-orange-500', description: 'Your life journey and longevity' },
  { key: 'career', label: 'Career & Success', icon: Briefcase, color: 'from-blue-500 to-cyan-500', description: 'Professional achievements' },
  { key: 'love', label: 'Love & Marriage', icon: Heart, color: 'from-pink-500 to-rose-500', description: 'Relationships and romance' },
  { key: 'children', label: 'Children & Family', icon: Baby, color: 'from-purple-500 to-violet-500', description: 'Family blessings' },
  { key: 'health', label: 'Health & Vitality', icon: Activity, color: 'from-green-500 to-emerald-500', description: 'Physical wellbeing' },
  { key: 'wealth', label: 'Wealth & Fortune', icon: Coins, color: 'from-yellow-500 to-amber-500', description: 'Financial prosperity' },
  { key: 'personality', label: 'Personality', icon: Brain, color: 'from-indigo-500 to-purple-500', description: 'Character traits' },
  { key: 'special', label: 'Special Signs', icon: Star, color: 'from-[#C84B32] to-[#F5B942]', description: 'Unique markings' }
];

// Reading animation messages
const READING_MESSAGES = [
  "Scanning your palm...",
  "Analyzing life line...",
  "Reading heart line patterns...",
  "Examining head line...",
  "Studying the mounts...",
  "Interpreting fate line...",
  "Decoding special markings...",
  "Calculating your destiny..."
];

export const PalmReadingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const { isStandalone } = usePWA();
  const { checkAccess, isPremium } = useFeatureAccess();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // States
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [readingMessage, setReadingMessage] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [palmReading, setPalmReading] = useState(null);
  const [expandedSection, setExpandedSection] = useState('life');
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureLocked, setFeatureLocked] = useState(false);

  // Handle file upload (from camera or gallery)
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
        setError('');
        setPalmReading(null);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so same file can be selected again
    e.target.value = '';
  }, []);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setPalmReading(null);
    setError('');
  }, []);

  // Analyze palm
  const analyzePalm = useCallback(async () => {
    if (!capturedImage) return;

    // Check feature access before analyzing
    if (!isPremium) {
      const access = await checkAccess('palm_reading');
      if (!access.allowed) {
        setFeatureLocked(true);
        setShowUpgradeModal(true);
        return;
      }
    }
    
    setAnalyzing(true);
    setError('');
    setReadingProgress(0);
    
    // Animated reading messages
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      setReadingMessage(READING_MESSAGES[messageIndex % READING_MESSAGES.length]);
      setReadingProgress((messageIndex + 1) * (100 / READING_MESSAGES.length));
      messageIndex++;
    }, 1500);

    try {
      const response = await axios.post(
        `${API}/palm-reading/analyze`,
        { image_data: capturedImage },
        { withCredentials: true, timeout: 60000 }
      );
      
      setPalmReading(response.data);
      setExpandedSection('life');
    } catch (err) {
      console.error('Palm analysis error:', err);
      if (err.response?.status === 403) {
        setFeatureLocked(true);
        setShowUpgradeModal(true);
      } else {
        setError(err.response?.data?.detail || 'Failed to analyze palm. Please try again with a clearer image.');
      }
    } finally {
      clearInterval(messageInterval);
      setAnalyzing(false);
      setReadingMessage('');
      setReadingProgress(100);
    }
  }, [capturedImage, isPremium, checkAccess]);

  return (
    <div className="min-h-screen cosmic-bg pb-28">
      {/* Background */}
      <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />

      {/* Navigation */}
      <ShubhHeader title="Palm Reading" showBack />

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Instructions Card */}
        {!capturedImage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="cosmic-card p-6 mb-6"
          >
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
                <Hand className="w-10 h-10 text-[#C84B32]" />
              </div>
              <h2 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
                Discover Your Destiny
              </h2>
              <p className="text-[#6B6560] mb-6 max-w-md mx-auto">
                Based on ancient Indian palmistry and scientific analysis, reveal insights about your life path, career, love, health, and more.
              </p>
              
              {/* Instructions */}
              <div className="bg-[#FFF9EF] rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
                <p className="text-[#C84B32] text-sm font-medium mb-2">For best results:</p>
                <ul className="space-y-2 text-sm text-[#6B6560]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#C84B32]">•</span>
                    Use your dominant hand (right for right-handed)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C84B32]">•</span>
                    Ensure good lighting on your palm
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C84B32]">•</span>
                    Keep your palm flat and fingers slightly spread
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C84B32]">•</span>
                    Capture the entire palm including lines
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {/* Camera Button - Opens camera directly */}
                <Button
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="camera-btn"
                  className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#C84B32]/90 hover:to-[#F5B942]/90 px-8"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photo
                </Button>
                {/* Hidden camera input - opens camera directly */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                {/* Gallery/Upload Button - Opens file picker */}
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
                  data-testid="upload-btn"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Upload from Gallery
                </Button>
                {/* Hidden file input - opens gallery/file picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Captured Image Preview */}
        {capturedImage && !palmReading && !analyzing && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cosmic-card p-4 mb-6"
          >
            <div className="relative rounded-xl overflow-hidden bg-white max-h-[50vh]">
              <img 
                src={capturedImage} 
                alt="Captured palm" 
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="flex gap-3 mt-4 justify-center">
              <Button
                onClick={retakePhoto}
                variant="outline"
                className="border-[#282622]/15 text-[#6B6560] hover:bg-[#FCE9D9]/60"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake
              </Button>
              <Button
                onClick={analyzePalm}
                data-testid="analyze-btn"
                className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#C84B32]/90 hover:to-[#F5B942]/90 px-8"
              >
                <Scan className="w-4 h-4 mr-2" />
                Read My Palm
              </Button>
            </div>

            {error && (
              <p className="text-red-600 text-sm text-center mt-3">{error}</p>
            )}
          </motion.div>
        )}

        {/* Analyzing Animation */}
        {analyzing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="cosmic-card p-8 text-center"
          >
            <div className="relative w-32 h-32 mx-auto mb-6">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-4 border-[#C84B32]/20" />
              <svg className="absolute inset-0 w-full h-full -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="4"
                  strokeDasharray={`${readingProgress * 3.77} 377`}
                  className="transition-all duration-500"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F5B942" />
                    <stop offset="100%" stopColor="#C84B32" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Inner hand icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Hand className="w-12 h-12 text-[#C84B32] animate-pulse" />
              </div>
              
              {/* Sparkles */}
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-[#C84B32] animate-bounce" />
              <Sparkles className="absolute -bottom-2 -left-2 w-5 h-5 text-[#C84B32] animate-bounce delay-75" />
            </div>
            
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-2">
              Reading Your Palm
            </h3>
            <p className="text-[#C84B32] animate-pulse">
              {readingMessage || 'Initializing...'}
            </p>
            <p className="text-[#6B6560] text-sm mt-2">
              This may take a moment...
            </p>
          </motion.div>
        )}

        {/* Palm Reading Results */}
        {palmReading && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Overview Card */}
              <div className="cosmic-card p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#C84B32]/10 to-transparent rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Crown className="w-8 h-8 text-[#C84B32]" />
                    <h2 className="font-['Fraunces'] text-2xl text-[#282622]">
                      Your Palm Reading
                    </h2>
                  </div>
                  
                  <p className="text-[#282622] leading-relaxed mb-4">
                    {palmReading.overview}
                  </p>
                  
                  {/* Hand Type */}
                  {palmReading.hand_type && (
                    <div className="inline-block px-4 py-2 rounded-full bg-[#C84B32]/10 border border-[#C84B32]/30">
                      <span className="text-[#C84B32] font-medium">
                        Hand Type: {palmReading.hand_type}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Section Cards */}
              <div className="space-y-3">
                {PALM_SECTIONS.map((section) => {
                  const sectionData = palmReading.sections?.[section.key];
                  if (!sectionData) return null;
                  
                  const isExpanded = expandedSection === section.key;
                  const Icon = section.icon;
                  
                  return (
                    <motion.div
                      key={section.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="cosmic-card overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedSection(isExpanded ? null : section.key)}
                        className="w-full p-4 flex items-center justify-between hover:bg-[#FCE9D9]/60 transition-colors"
                        data-testid={`section-${section.key}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="text-[#282622] font-medium">{section.label}</p>
                            <p className="text-[#8A8279] text-xs">{section.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {sectionData.rating && (
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < sectionData.rating ? 'text-[#C84B32] fill-[#F5B942]' : 'text-[#282622]/15'}`} 
                                />
                              ))}
                            </div>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#6B6560]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#6B6560]" />
                          )}
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 border-t border-[#282622]/10 pt-3">
                              <p className="text-[#282622] leading-relaxed mb-3">
                                {sectionData.reading}
                              </p>
                              
                              {sectionData.insights && sectionData.insights.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-[#C84B32] text-sm font-medium">Key Insights:</p>
                                  <ul className="space-y-1">
                                    {sectionData.insights.map((insight, i) => (
                                      <li key={i} className="flex items-start gap-2 text-sm text-[#6B6560]">
                                        <Sparkles className="w-3 h-3 text-[#C84B32] mt-1 flex-shrink-0" />
                                        {insight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>

              {/* Lines Detected */}
              {palmReading.lines_detected && (
                <div className="cosmic-card p-6">
                  <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">
                    Lines Detected
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Object.entries(palmReading.lines_detected).map(([line, description]) => (
                      <div key={line} className="p-3 rounded-xl bg-[#FFF9EF]">
                        <p className="text-[#C84B32] text-sm font-medium capitalize mb-1">
                          {line.replace('_', ' ')}
                        </p>
                        <p className="text-[#6B6560] text-xs">{description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Reading Button */}
              <div className="text-center pt-4 flex flex-col items-center gap-3">
                <div className="flex gap-3">
                  <Button
                    onClick={retakePhoto}
                    variant="outline"
                    className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New Reading
                  </Button>
                  
                  {/* Share Palm Reading */}
                  <ShareButton 
                    type="palm"
                    data={{
                      summary: palmReading?.overall_destiny || palmReading?.life_path?.description,
                      lifeLine: palmReading?.life_path?.description
                    }}
                  />
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-[#8A8279] text-xs text-center px-4">
                This reading is based on traditional palmistry principles and AI analysis. 
                It is for entertainment and self-reflection purposes only.
              </p>
            </motion.div>
          </AnimatePresence>
        )}
      </main>

      {/* Upgrade Modal */}
      <QuotaExhaustedPopup 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature="palm_reading"
      />

      {/* Upgrade Banner */}
      {featureLocked && !showUpgradeModal && (
        <div className="fixed bottom-20 left-4 right-4 z-40">
          <UpgradeBanner 
            feature="palm_reading"
            onUpgradeClick={() => setShowUpgradeModal(true)}
          />
        </div>
      )}
      <ShubhBottomNav />
    </div>
  );
};

export default PalmReadingPage;
